import { useEffect, useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import FormControl from 'react-bootstrap/FormControl';
import { useAuth } from '../../context/AuthContext';
import { unFreezeMyWeek } from '../../services/apiClient';
import { toErrorState } from '../../utils/errorMessage';
import { reasonsToUnfreeze, type UnfreezeReason } from '../../utils/validations';
import type { ErrorState, SelectedDate } from '../../types';

/**
 * Port of `commonComponents/unfreezeModal.jsx` — raises a ServiceNow ticket to unfreeze
 * a past week.
 *
 * The "Go To Anytime" link was a hardcoded absolute URL in the original
 * (unfreezeModal.jsx:522), including the catalog item's sys_id, so every environment
 * pointed at production ServiceNow. It now comes from the runtime config
 * (`serviceNowUnfreezeUrl`), seeded with that same literal — same behavior today,
 * configurable per environment from now on. `week_start_date` is still appended here.
 *
 * Dropped: the empty `componentDidUpdate`, the unused `Button`/`Modal`/`DropdownItem`
 * imports, the `CheckInLoadingPopup` import that was never rendered, and the no-op
 * `e.target.value = e.target.value` line.
 */

export interface ProjectOption {
  gteProjectId: number;
  name: string;
  [key: string]: unknown;
}

export function UnfreezeModal({
  projectList,
  selectedDate,
  loggedUserGteEmployeeNo,
  operationalUserGteEmployeeNo,
  showUnfreezeModal,
  onError,
}: {
  projectList: ProjectOption[];
  selectedDate: SelectedDate;
  loggedUserGteEmployeeNo: number;
  operationalUserGteEmployeeNo: number;
  showUnfreezeModal: (show: boolean) => void;
  onError: (error: ErrorState) => void;
}): JSX.Element {
  const { config } = useAuth();

  const [show, setShow] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [selectedOption, setSelectedOption] = useState<ProjectOption | null>(null);
  const [benchOrNonProject, setBenchOrNonProject] = useState(false);
  const [searchValueForUnfreeze, setSearchValueForUnfreeze] = useState('');
  const [selectedOptionForUnfreeze, setSelectedOptionForUnfreeze] = useState<UnfreezeReason>(
    reasonsToUnfreeze[0] as UnfreezeReason,
  );
  const [isSelectAnyOtherReason, setIsSelectAnyOtherReason] = useState(false);
  const [commentOfAnyOtherReason, setCommentOfAnyOtherReason] = useState('');
  const [responseRecieved, setResponseRecieved] = useState(false);
  const [ticketNo, setTicketNo] = useState<string>('');
  const [isRequiredFieldMissed, setIsRequiredFieldMissed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialState, setInitialState] = useState(true);

  useEffect(() => {
    if (projectList?.length > 0) {
      setSelectedOption(projectList[0] as ProjectOption);
    }
    // The 1s window during which the heading auto-focuses itself. It exists so focus
    // lands on the modal after it animates in rather than during.
    const timer = setTimeout(() => setInitialState(false), 1000);
    return () => clearTimeout(timer);
    // Mount-only, as componentDidMount was.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = (): void => {
    setShow(false);
    showUnfreezeModal(false);
  };

  const handleOptionSelect = (option: ProjectOption): void => {
    setSelectedOption({ gteProjectId: option?.gteProjectId, name: option?.name });
    setSearchValue('');
  };

  const handleCheckBoxChangeEvent = (checked: boolean): void => {
    setBenchOrNonProject(checked);
    if (checked) {
      // -1 is the "no project" sentinel the BFF expects.
      setSelectedOption({ gteProjectId: -1, name: 'Non-Project' });
      setSearchValue('');
    } else {
      setSelectedOption((projectList[0] ?? null) as ProjectOption | null);
    }
  };

  const handleOptionSelectForUnfreeze = (option: UnfreezeReason): void => {
    setSelectedOptionForUnfreeze({ key: option?.key, value: option?.value });
    setSearchValueForUnfreeze('');

    if (option?.key === 7) {
      setIsSelectAnyOtherReason(true);
    } else {
      // The original's guard here is `option?.key !== 7 || option?.key !== 1`, which is
      // true for every key — a tautology. Kept as an unconditional clear, which is what
      // it actually does.
      setIsRequiredFieldMissed(false);
      setIsSelectAnyOtherReason(false);
      setCommentOfAnyOtherReason('');
    }
  };

  const saveUnFreezeMyWeekData = (value: Record<string, unknown>): void => {
    setLoading(true);
    unFreezeMyWeek(value)
      .then((response) => {
        setResponseRecieved(true);
        setTicketNo(
          ((response as { result?: { number?: string } })?.result?.number ?? '') as string,
        );
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        onError(toErrorState(error));
      });
  };

  const createResponseValue = (): void => {
    const value: Record<string, unknown> = {
      gteProjectId: parseInt(String(selectedOption?.gteProjectId)),
      year: Number(selectedDate?.year),
      weekNo: Number(selectedDate?.week),
      gteEmployeeNo: Number(operationalUserGteEmployeeNo),
      reasonToUnfreeze: selectedOptionForUnfreeze?.value,
      loggedUserGteEmployeeId: Number(loggedUserGteEmployeeNo),
    };
    if (isSelectAnyOtherReason) {
      value.comment = commentOfAnyOtherReason;
    }
    saveUnFreezeMyWeekData(value);
  };

  const handleSubmitOnModel = (): void => {
    const key = selectedOptionForUnfreeze.key;

    if (key === 1) {
      // "~~None~~" is still selected — nothing to submit.
      setIsRequiredFieldMissed(true);
    } else if (key === 7) {
      if (commentOfAnyOtherReason.trim().length > 0) {
        createResponseValue();
      } else {
        setIsRequiredFieldMissed(true);
      }
    } else if (!isRequiredFieldMissed) {
      createResponseValue();
    }
  };

  const filteredOptions = projectList?.filter(
    (option) =>
      option?.gteProjectId !== -1 &&
      option?.name.toLowerCase().includes(searchValue.toLowerCase()),
  );

  const filteredOptionsForUnfreeze = reasonsToUnfreeze?.filter((option) =>
    option?.value.toLowerCase().includes(searchValueForUnfreeze.toLowerCase()),
  );

  const modelStyle = {
    display: 'block',
    backgroundColor: 'rgba(0,0,0,0.8)',
  } as const;

  const serviceNowHref = `${config?.serviceNowUnfreezeUrl ?? ''}&week_start_date=${
    selectedDate?.firstDayofWeekForUnfreeze
  }`;

  return (
    <>
      <div>
        <div className="modal show fade unfreeze-popup" style={modelStyle}>
          <div className="modal-dialog modal-width">
            <div className="modal-content">
              <div className="modal-header">
                <span
                  tabIndex={0}
                  ref={(inputElement) => {
                    if (inputElement) {
                      if (show && initialState) inputElement.focus();
                      if (show && loading) inputElement.focus();
                    }
                  }}
                >
                  {' '}
                  Unfreeze My Week
                </span>
                <button
                  tabIndex={0}
                  title="close"
                  disabled={loading}
                  type="button"
                  className="btn-close"
                  onClick={() => handleClose()}
                ></button>
              </div>

              <div className="modal-body">
                <div className="row unfreeze-header">
                  <div className="col-8" tabIndex={0} title="Week To Unfreeze Time">
                    {' '}
                    Week To Unfreeze Time
                  </div>

                  <div className="col-4">
                    <div
                      className="unf-date"
                      tabIndex={0}
                      title={`${selectedDate.year} year  ${selectedDate.week} week  `}
                    >
                      {selectedDate.year}-{selectedDate.week}
                    </div>
                  </div>
                </div>

                {!benchOrNonProject && (
                  <div className="row">
                    <div className="col-12">
                      <span>
                        <b tabIndex={0}>Project To Unfreeze</b>
                      </span>
                      <Dropdown>
                        <Dropdown.Toggle
                          disabled={loading || responseRecieved}
                          variant="light"
                          id="dropdown-basic"
                          tabIndex={0}
                          title={selectedOption ? selectedOption?.name : ''}
                        >
                          {selectedOption ? selectedOption?.name : ''}
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                          <FormControl
                            className="unfreeze"
                            type="text"
                            placeholder="Search..."
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            tabIndex={0}
                          />
                          {filteredOptions?.map((option) => (
                            <Dropdown.Item
                              tabIndex={0}
                              eventKey={option.gteProjectId}
                              title={option?.name}
                              key={option.gteProjectId}
                              onClick={() => handleOptionSelect(option)}
                            >
                              {option?.name}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </div>
                )}

                {/* Only offered when acting as yourself — a delegate cannot mark
                    someone else as bench. */}
                {loggedUserGteEmployeeNo === operationalUserGteEmployeeNo && (
                  <div className="row select-type">
                    <div className="col-12">
                      <div
                        key={1}
                        className="form-check"
                        tabIndex={0}
                        title={`${benchOrNonProject} Bench Employee or No Project Allocation`}
                      >
                        <input
                          disabled={loading}
                          className="form-check-input"
                          type="checkbox"
                          checked={benchOrNonProject}
                          id="flexCheckDefault"
                          onChange={(e) => handleCheckBoxChangeEvent(e.target.checked)}
                        />
                        <label className="form-check-label">
                          <b>Bench Employee or No Project Allocation</b>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                <div className="row">
                  <div className="col-12">
                    <span>
                      <b tabIndex={0}>Reason To Unfreeze</b>
                    </span>
                    {selectedOptionForUnfreeze?.key === 1 && (
                      <span className="text-danger m-2" tabIndex={0}>
                        * Required
                      </span>
                    )}
                    <Dropdown>
                      <Dropdown.Toggle
                        ref={(inputElement: HTMLElement | null) => {
                          if (inputElement) {
                            if (!isSelectAnyOtherReason && isRequiredFieldMissed) {
                              inputElement.focus();
                            }
                          }
                        }}
                        disabled={loading || responseRecieved}
                        variant="light"
                        id="dropdown-basic"
                        tabIndex={0}
                        title={selectedOptionForUnfreeze ? selectedOptionForUnfreeze?.value : ''}
                      >
                        {selectedOptionForUnfreeze ? selectedOptionForUnfreeze?.value : ''}
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        <FormControl
                          className="unfreeze"
                          tabIndex={0}
                          type="text"
                          placeholder="Search..."
                          value={searchValueForUnfreeze}
                          onChange={(e) => setSearchValueForUnfreeze(e.target.value)}
                        />
                        {/* key 1 ("~~None~~") is filtered out of the list but remains
                            the initial value, which is what makes "* Required" show. */}
                        {filteredOptionsForUnfreeze?.map(
                          (option) =>
                            option?.key !== 1 && (
                              <Dropdown.Item
                                eventKey={option?.key}
                                title={option?.value}
                                key={option?.key}
                                onClick={() => handleOptionSelectForUnfreeze(option)}
                                tabIndex={0}
                              >
                                {option?.value}
                              </Dropdown.Item>
                            ),
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>

                {isSelectAnyOtherReason && (
                  <div className="row">
                    <div className="col-12">
                      <span tabIndex={0}>
                        <b>Please Describe</b>
                      </span>
                      {commentOfAnyOtherReason?.length === 0 && (
                        <span
                          className="text-danger m-2"
                          tabIndex={0}
                          ref={(inputElement) => {
                            if (inputElement) {
                              if (
                                isSelectAnyOtherReason &&
                                commentOfAnyOtherReason?.length === 0 &&
                                isRequiredFieldMissed
                              ) {
                                inputElement.focus();
                              }
                            }
                          }}
                        >
                          * Required
                        </span>
                      )}
                    </div>
                    <div className="col-12">
                      <textarea
                        disabled={loading || responseRecieved}
                        tabIndex={0}
                        className="textarea"
                        id="commentForAnyOtherReason"
                        name="commentForAnyOtherReason"
                        rows={4}
                        cols={50}
                        onChange={(e) => {
                          e.preventDefault();
                          setCommentOfAnyOtherReason(e.target.value);
                          setIsRequiredFieldMissed(false);
                        }}
                        onBlur={(e) => {
                          e.preventDefault();
                          setCommentOfAnyOtherReason(e.target.value);
                          setIsRequiredFieldMissed(false);
                        }}
                      ></textarea>
                    </div>
                  </div>
                )}

                {responseRecieved && (
                  <div
                    className="unfreeze-ticket"
                    ref={(inputElement) => {
                      if (inputElement) {
                        if (!loading && responseRecieved) inputElement.focus();
                      }
                    }}
                    tabIndex={0}
                    title={`Response recieved: Your Ticket Number is ${ticketNo}`}
                  >
                    <div className="row ">
                      <div className="item col-8 mx-auto">Your Ticket No is :</div>
                      <div className="item col-4 mx-auto">{ticketNo}</div>
                    </div>
                  </div>
                )}

                {loading && (
                  <div
                    tabIndex={0}
                    className="unfreeze-ticket"
                    ref={(inputElement) => {
                      if (inputElement) {
                        if (show && loading) inputElement.focus();
                      }
                    }}
                  >
                    <div className="row " tabIndex={0}>
                      Please wait for a moment.. Your ticket is generating
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <a
                  className="unfreeze-link"
                  tabIndex={0}
                  title="Redirect To ServiceNow"
                  target="_blank"
                  rel="noreferrer"
                  href={serviceNowHref}
                >
                  Go To Anytime
                </a>

                {responseRecieved ? (
                  <button
                    tabIndex={0}
                    title="Close"
                    type="button"
                    className="snow-close-btn"
                    onClick={() => handleClose()}
                  >
                    Close
                  </button>
                ) : (
                  <button
                    ref={(inputElement) => {
                      if (inputElement) {
                        if (loading) {
                          inputElement.focus();
                          inputElement.click();
                        }
                      }
                    }}
                    disabled={loading}
                    tabIndex={0}
                    title="Submit Request"
                    type="button"
                    className="snow-btn"
                    onClick={() => handleSubmitOnModel()}
                  >
                    Submit Request
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
